"use client";
import { useMemo } from "react";
import { FolderTreeItem } from "@zapier/client";
import {
  CopyToAnotherAccountModal,
  FolderModal,
  Table,
  useFolderAndZapsModalStates,
  useRouterParams,
} from "@zapier/shared";
import { FolderTypeQueryState } from "@zapier/utils";
import { FoldersOrder } from "@zapier/bff";
import FolderRow from "./FolderRow";
import { useShowEmptyFolderTable } from "./useShowEmptyFolderTable";
import { FolderModals } from "../../FolderModals";
import { EmptyFolderTable } from "./EmptyFolderTable";
import styles from "./FolderTable.module.css";
import {
  BetaColumnHeader,
  SortDescriptor,
  SortDirection,
} from "@zapier/design-system";

interface FolderTableProps {
  ariaLabel: string;
  folders: FolderTreeItem[];
  folderType?: FolderTypeQueryState | null;
  isFoldersExpandedByDefault?: boolean;
  isLoading?: boolean;
  onSortChange?: (sortDescriptor: SortDescriptor) => void;
  order?: FoldersOrder;
  searchQuery?: string | null;
}

const FolderTable = ({
  ariaLabel,
  folders,
  folderType,
  isFoldersExpandedByDefault,
  isLoading,
  onSortChange,
  order,
  searchQuery,
}: FolderTableProps) => {
  const columns: BetaColumnHeader[] = useMemo(
    () => [
      {
        label: "Automations",
        sortKey: "asset_count",
        allowsSorting: true,
        className: styles.assetCountColumn,
      },
    ],
    [],
  );
  const {
    currentFolder,
    setCurrentFolder,
    currentFolderModal,
    setCurrentFolderModal,
  } = useFolderAndZapsModalStates();
  const { owner: selectedOwner } = useRouterParams();

  // default sorting by title in ascending order
  const sortDescriptor = {
    column: "title",
    direction: "ascending" as SortDirection,
  };

  // update sortDescriptor if order is provided
  if (order) {
    sortDescriptor.column = order.replace("-", "");
    sortDescriptor.direction = (
      order[0] === "-" ? "descending" : "ascending"
    ) as SortDirection;
  }

  const emptyTableType = useShowEmptyFolderTable({
    sortDescriptor,
    order,
    searchQuery,
    hasFolders: folders.length > 0,
    isLoading: !!isLoading,
    selectedOwner,
  });

  if (emptyTableType) {
    return (
      <EmptyFolderTable
        tableType={emptyTableType}
        folderType={folderType}
        setCurrentFolderModal={setCurrentFolderModal}
        currentFolderModal={currentFolderModal}
      />
    );
  }

  return (
    <>
      <Table
        allowSortByTitle={true}
        ariaLabel={ariaLabel}
        className={styles.folderTable}
        columns={columns}
        isLoading={isLoading}
        onSortChange={onSortChange}
        showCheckboxes={false}
        showOwner={true}
        // No share action wired on these rows — don't reserve share-button
        // width so the Options column stays tight and consistent.
        showShareButton={false}
        sortDescriptor={sortDescriptor}
      >
        {folders.map((folder, index) => (
          <FolderRow
            folder={folder}
            folderLevel={0}
            isFoldersExpandedByDefault={isFoldersExpandedByDefault}
            key={index}
            searchQuery={searchQuery}
            includeSelectionColumn={false}
            setCurrentModal={setCurrentFolderModal}
            setCurrentFolder={setCurrentFolder}
          />
        ))}
      </Table>

      <FolderModals
        currentModal={currentFolderModal}
        setCurrentModal={setCurrentFolderModal}
        currentFolder={currentFolder}
        setCurrentFolder={setCurrentFolder}
      />
      <CopyToAnotherAccountModal
        isOpen={currentFolderModal === FolderModal.CopyToAnotherAccount}
        setIsOpen={(isOpen) => {
          if (!isOpen) {
            setCurrentFolderModal(undefined);
            setCurrentFolder(undefined);
          }
        }}
        assets={[]}
        copyEntryPoint="row_kebab"
        folderIds={currentFolder?.id ? [currentFolder.id] : undefined}
      />
    </>
  );
};

export { FolderTable };
